import { PublicClientApplication } from "@azure/msal-browser";
import { authConfig } from "./authConfig";

// Shared MSAL instance. The task pane and the sign-in dialog run as separate
// same-origin pages, so they share the localStorage token cache: the dialog
// writes the account/tokens on interactive sign-in, and the task pane can then
// read them back to restore the session silently on reopen (REQ-AUTH-005).
export const msalInstance = new PublicClientApplication({
  auth: {
    clientId: authConfig.clientId,
    authority: authConfig.msalAuthority,
    redirectUri: `${window.location.origin}/dialog.html`,
  },
  cache: {
    cacheLocation: "localStorage",
  },
});

let initPromise: Promise<void> | undefined;
export function ensureMsalInitialized(): Promise<void> {
  if (!initPromise) {
    initPromise = msalInstance.initialize();
  }
  return initPromise;
}

/** Returns a cached Graph token for a previously signed-in account, or null. */
export async function acquireMsalTokenSilent(): Promise<string | null> {
  await ensureMsalInitialized();
  const account = msalInstance.getAllAccounts()[0];
  if (!account) {
    return null;
  }
  try {
    const result = await msalInstance.acquireTokenSilent({
      account,
      scopes: authConfig.graphScopes,
    });
    return result.accessToken;
  } catch {
    return null;
  }
}

/**
 * Clears the cached MSAL account/tokens on sign-out. Never throws — when the
 * browser blocks storage for the add-in iframe there is nothing cached to
 * clear, and sign-out must still complete.
 */
export async function clearMsalCache(): Promise<void> {
  try {
    await ensureMsalInitialized();
    const account = msalInstance.getAllAccounts()[0];
    if (account) {
      await msalInstance.clearCache({ account });
    }
  } catch {
    // no persisted MSAL state to clear
  }
}
